import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  Navigation, MapPin, Clock, Car, Truck, Accessibility, 
  ArrowLeft, Phone, User, ShieldCheck, AlertCircle
} from "lucide-react";
import MockMap from "@/components/MockMap";
import RideProgressTimeline from "@/components/RideProgressTimeline";
import LiveFleetMap from "@/components/LiveFleetMap";

export default function RideDetails() {
  const { rideId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ride, setRide] = useState(null);
  const [pinInput, setPinInput] = useState("");
  const [pinVerified, setPinVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [clientLocation, setClientLocation] = useState(null);

  // Get user location for map centering
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setClientLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => console.log("Location access denied")
      );
    }
  }, []);

  useEffect(() => {
    fetchRide();
    const interval = setInterval(fetchRide, 5000);
    return () => clearInterval(interval);
  }, [rideId]);

  const fetchRide = async () => {
    try {
      const response = await apiClient.get(`/rides/${rideId}`);
      setRide(response.data);
    } catch (error) {
      toast.error("Failed to fetch ride details");
      navigate(-1);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyPin = async () => {
    try {
      await apiClient.post(`/rides/${rideId}/verify-pin`, { pin_code: pinInput });
      setPinVerified(true);
      toast.success("PIN verified successfully!");
    } catch (error) {
      toast.error("Invalid PIN");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      requested: "bg-orange-100 text-orange-800",
      approved: "bg-blue-100 text-blue-800",
      rejected: "bg-red-100 text-red-800",
      scheduled: "bg-purple-100 text-purple-800",
      in_progress: "bg-indigo-100 text-indigo-800",
      arrived: "bg-lime-100 text-lime-800",
      completed: "bg-green-100 text-green-800"
    };
    return colors[status] || "bg-slate-100 text-slate-800";
  };

  const vehicleIcons = { car: Car, van: Truck, handicap_van: Accessibility };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-900"></div>
      </div>
    );
  }

  if (!ride) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Ride not found</p>
      </div>
    );
  }

  const VehicleIcon = vehicleIcons[ride.vehicle_type] || Car;
  const isClient = user?.role === "client";
  const isDriver = user?.role === "driver";
  const showPin = isClient || (isDriver && ride.driver_id === user?.id) || user?.role === "employer";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="mr-4"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-900 rounded-xl flex items-center justify-center">
                <Navigation className="w-5 h-5 text-lime-400" />
              </div>
              <span className="text-xl font-heading font-black text-indigo-900">Ride Details</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Status Banner */}
        <Card className="mb-6 border-0 shadow-lg overflow-hidden" data-testid="ride-status-card">
          <div className={`h-2 ${
            ride.status === "requested" ? "bg-orange-400" :
            ride.status === "approved" ? "bg-blue-400" :
            ride.status === "rejected" ? "bg-red-400" :
            ride.status === "scheduled" ? "bg-purple-400" :
            ride.status === "in_progress" ? "bg-indigo-400" :
            ride.status === "arrived" ? "bg-lime-400" :
            "bg-green-400"
          }`} />
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Badge className={`${getStatusColor(ride.status)} text-sm px-3 py-1`}>
                {ride.status === "requested" ? "PENDING APPROVAL" : 
                 ride.status === "in_progress" ? "DRIVER EN ROUTE" :
                 ride.status.replace('_', ' ').toUpperCase()}
              </Badge>
              <div className="flex items-center gap-2">
                <VehicleIcon className="w-5 h-5 text-slate-400" />
                <span className="text-slate-600 capitalize">{ride.vehicle_type.replace('_', ' ')}</span>
              </div>
            </div>

            {/* ETA Display */}
            {ride.status === "in_progress" && ride.eta_minutes !== null && (
              <div className="bg-indigo-50 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-indigo-600 text-sm font-medium">Driver is on the way!</p>
                    <p className="text-indigo-900 text-2xl font-heading font-bold">
                      ETA: {ride.eta_minutes} minutes
                    </p>
                  </div>
                  <Clock className="w-10 h-10 text-indigo-300" />
                </div>
              </div>
            )}

            {/* Arrived Banner */}
            {ride.status === "arrived" && (
              <div className="bg-lime-50 rounded-xl p-4 mb-4 border border-lime-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-lime-400 rounded-full flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-lime-800 font-bold">Driver Has Arrived!</p>
                    <p className="text-lime-600 text-sm">Please verify with your 4-digit PIN</p>
                  </div>
                </div>
              </div>
            )}

            {/* Date & Time */}
            <div className="flex items-center gap-2 text-slate-600">
              <Clock className="w-4 h-4" />
              <span>{ride.pickup_date} at {ride.pickup_time}</span>
            </div>
          </CardContent>
        </Card>

        {/* Ride Progress Timeline */}
        <Card className="mb-6 border-slate-200" data-testid="ride-timeline-card">
          <CardContent className="p-4">
            <RideProgressTimeline 
              ride={ride}
              variant="light"
              compact={false}
              showDriver={true}
              showETA={true}
            />
          </CardContent>
        </Card>

        {/* Live Map - for active rides */}
        {["scheduled", "in_progress", "arrived"].includes(ride.status) && (
          <Card className="mb-6 border-slate-200" data-testid="ride-map-card">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Navigation className="w-5 h-5 text-cyan-500" />
                {ride.status === "in_progress" ? "Live Tracking" : "Route Preview"}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="h-72 rounded-b-xl overflow-hidden">
                <LiveFleetMap 
                  mode={user?.role === "driver" ? "driver" : "client"}
                  currentRide={ride}
                  clientLocation={clientLocation}
                  height="100%"
                  variant="light"
                  showControls={true}
                />
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Locations */}
          <Card className="border-slate-200" data-testid="locations-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Route</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {ride.pickup_locations.map((loc, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-blue-600">{index + 1}</span>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs uppercase tracking-wide">Pickup {index + 1}</p>
                    <p className="font-medium text-slate-900">{loc.address}</p>
                  </div>
                </div>
              ))}
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-red-600" />
                </div>
                <div>
                  <p className="text-slate-500 text-xs uppercase tracking-wide">Dropoff</p>
                  <p className="font-medium text-slate-900">{ride.dropoff_address}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Driver Info */}
          <Card className="border-slate-200" data-testid="driver-info-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg">
                {isDriver ? "Client Info" : "Driver Info"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isDriver ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-indigo-100 rounded-full flex items-center justify-center">
                      <User className="w-7 h-7 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{ride.client_name}</p>
                      <p className="text-slate-500">{ride.client_phone}</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => window.open(`tel:${ride.client_phone}`)}
                    data-testid="call-client-details-button"
                  >
                    <Phone className="w-4 h-4 mr-2" /> Call Client
                  </Button>
                </div>
              ) : ride.driver_name ? (
                <div className="space-y-4">
                  {/* Driver Photo & Name */}
                  <div className="flex items-center gap-4">
                    {ride.driver_picture ? (
                      <img 
                        src={ride.driver_picture} 
                        alt={ride.driver_name}
                        className="w-16 h-16 rounded-full object-cover border-2 border-indigo-200"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className={`w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center ${ride.driver_picture ? 'hidden' : ''}`}>
                      <User className="w-8 h-8 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-lg">{ride.driver_name}</p>
                      <p className="text-indigo-600 font-medium">{ride.driver_phone || "No phone"}</p>
                    </div>
                  </div>

                  {/* Vehicle Info */}
                  {(ride.driver_vehicle_make || ride.driver_vehicle_model) && (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                      <p className="text-slate-500 text-xs uppercase tracking-wide mb-2">Vehicle</p>
                      <div className="flex items-center gap-3">
                        <Car className="w-6 h-6 text-indigo-600" />
                        <div>
                          <p className="font-semibold text-slate-900">
                            {[ride.driver_vehicle_color, ride.driver_vehicle_make, ride.driver_vehicle_model].filter(Boolean).join(' ')}
                          </p>
                          {ride.driver_vehicle_plate && (
                            <p className="text-slate-600 font-mono text-sm">{ride.driver_vehicle_plate}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ETA Display for Client */}
                  {ride.eta_minutes !== null && ride.status === "in_progress" && (
                    <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-indigo-600 text-sm font-medium">Estimated Arrival</p>
                          <p className="text-2xl font-heading font-bold text-indigo-900">{ride.eta_minutes} minutes</p>
                        </div>
                        <Clock className="w-8 h-8 text-indigo-300" />
                      </div>
                    </div>
                  )}

                  {ride.driver_phone && (
                    <Button 
                      variant="outline" 
                      className="w-full border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                      onClick={() => window.open(`tel:${ride.driver_phone}`)}
                      data-testid="call-driver-button"
                    >
                      <Phone className="w-4 h-4 mr-2" /> Call Driver
                    </Button>
                  )}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Clock className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500">No driver assigned yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* PIN Section */}
        {showPin && (
          <Card className="mt-6 border-slate-200" data-testid="pin-section-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-indigo-600" />
                Verification PIN
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isClient || user?.role === "employer" ? (
                <div className="bg-indigo-50 rounded-xl p-6 text-center">
                  <p className="text-indigo-600 text-sm mb-2">Your PIN Code</p>
                  <p className="text-4xl font-mono font-bold text-indigo-900 tracking-[0.5em]">
                    {ride.pin_code}
                  </p>
                  <p className="text-indigo-500 text-xs mt-2">Share this PIN with your driver upon arrival</p>
                </div>
              ) : isDriver ? (
                pinVerified ? (
                  <div className="bg-green-50 rounded-xl p-6 text-center border border-green-200">
                    <ShieldCheck className="w-12 h-12 text-green-500 mx-auto mb-2" />
                    <p className="text-green-800 font-bold">PIN Verified!</p>
                    <p className="text-green-600 text-sm">Identity confirmed</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-slate-600 text-sm">Enter the client's 4-digit PIN to verify identity</p>
                    <div className="flex gap-3">
                      <Input
                        type="text"
                        maxLength={4}
                        placeholder="Enter PIN"
                        value={pinInput}
                        onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
                        className="text-center text-2xl font-mono tracking-[0.3em] h-14"
                        data-testid="verify-pin-input"
                      />
                      <Button 
                        onClick={handleVerifyPin}
                        className="bg-indigo-900 hover:bg-indigo-800 h-14 px-6"
                        disabled={pinInput.length !== 4}
                        data-testid="verify-pin-button"
                      >
                        Verify
                      </Button>
                    </div>
                  </div>
                )
              ) : null}
            </CardContent>
          </Card>
        )}

        {/* Special Requests */}
        {ride.special_requests && (
          <Card className="mt-6 border-slate-200" data-testid="special-requests-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
                Special Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700">{ride.special_requests}</p>
            </CardContent>
          </Card>
        )}

        {/* Driver Notes */}
        {ride.driver_notes && (
          <Card className="mt-6 border-slate-200" data-testid="driver-notes-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Driver Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700">{ride.driver_notes}</p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
